import React, { useState, useCallback, useEffect } from 'react';
import './Refund.css';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../context/I18nContext';
import meleeWeapons from '../data/melee_weapons.json';

export default function Refund() {
  const { apiFetch, hasPermission } = useAuth();
  const { t } = useI18n();

  const [searchInput, setSearchInput] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [allItems, setAllItems] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [itemSearch, setItemSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [lastGiveResults, setLastGiveResults] = useState(null);
  const [registeringWeapons, setRegisteringWeapons] = useState(false);
  const [registrationMessage, setRegistrationMessage] = useState(null);

  const loadItems = useCallback(async () => {
    try {
      setError(null);
      const res = await apiFetch('/api/refund/items');
      let data = null;
      const contentType = res && res.headers && res.headers.get ? res.headers.get('content-type') || '' : '';
      if (contentType.includes('application/json')) {
        data = await res.json();
      } else {
        const text = await res.text();
        try {
          data = JSON.parse(text);
        } catch (e) {
          // Non-JSON response (likely HTML error page)
          throw new Error(text || `HTTP ${res.status}`);
        }
      }

      if (res.ok) {
        setAllItems(data.data || []);
      } else {
        setError(data && data.error ? data.error : 'Failed to load items');
      }
    } catch (err) {
      setError(t('refund.errorLoadingItems', { error: err.message }));
    }
  }, [apiFetch, t]);

  // Load all items on component mount
  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const searchPlayers = useCallback(async (query) => {
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }

    try {
      setError(null);
      const res = await apiFetch(`/api/lookup/player?search=${encodeURIComponent(query)}`);
      let data = null;
      const contentType = res && res.headers && res.headers.get ? res.headers.get('content-type') || '' : '';
      if (contentType.includes('application/json')) {
        data = await res.json();
      } else {
        const text = await res.text();
        try { data = JSON.parse(text); } catch (e) { throw new Error(text || `HTTP ${res.status}`); }
      }

      if (res.ok) {
        const formatted = (data || []).map(p => ({
          identifier: p.identifier,
          name: `${p.firstname} ${p.lastname}`
        }));
        setSearchResults(formatted);
      } else {
        setError(data && data.error ? data.error : t('refund.failedToSearchPlayers'));
        setSearchResults([]);
      }
    } catch (err) {
      setError(t('refund.errorSearchingPlayers', { error: err.message }));
      setSearchResults([]);
    }
  }, [apiFetch, t]);

  const handlePlayerSearch = (e) => {
    const value = e.target.value;
    setSearchInput(value);
    searchPlayers(value);
  };

  const selectPlayer = (player) => {
    setSelectedPlayer(player);
    setSearchInput('');
    setSearchResults([]);
    setRegistrationMessage(null);
  };

  const clearPlayer = () => {
    setSelectedPlayer(null);
    setSearchInput('');
    setSearchResults([]);
    setSelectedItems([]);
    setRegistrationMessage(null);
  };

  const filteredItems = allItems.filter(item =>
    item.label.toLowerCase().includes(itemSearch.toLowerCase()) ||
    item.item_name.toLowerCase().includes(itemSearch.toLowerCase())
  );

  const isWeaponItem = (value) => String(value || '').toLowerCase().startsWith('weapon_');
  const meleeSet = new Set((meleeWeapons || []).map(s => String(s).toLowerCase()));

  const isMeleeItem = (value) => {
    if (!value) return false;
    const n = String(value).toLowerCase();
    // Check generated list first (case-insensitive)
    if (meleeSet.has(n)) return true;
    // Fallback heuristic: common substrings
    return ['knife','bat','club','machete','crowbar','hammer','knuckle','wrench','bottle'].some(s => n.includes(s));
  };
  const isFirearmItem = (value) => isWeaponItem(value) && !isMeleeItem(value);

  const firearmItems = selectedItems.filter((item) => item.isFirearm); // firearms specifically (for registration)
  const pendingWeaponItems = firearmItems.filter((item) => !item.serial);

  const canRefundWeapons = hasPermission('refund.weapons');

  const addItem = (item) => {
    // Disallow adding weapon items unless user has explicit permission
    if (isFirearmItem(item.item_name) && !canRefundWeapons) {
      setError(t('refund.noWeaponPermission'));
      return;
    }

    const exists = selectedItems.find(i => i.name === item.item_name);
    if (exists) {
      setError(t('refund.itemAlreadyAdded'));
      return;
    }
    setSelectedItems([...selectedItems, {
      name: item.item_name,
      label: item.label,
      count: 1,
      isWeapon: isWeaponItem(item.item_name),
      isFirearm: isFirearmItem(item.item_name),
      serial: null,
      metadata: null
    }]);
  };

  const removeItem = (itemName) => {
    setSelectedItems(selectedItems.filter(i => i.name !== itemName));
  };

  const updateItemCount = (itemName, count) => {
    const newCount = Math.max(1, parseInt(count) || 1);
    setSelectedItems(selectedItems.map(i =>
      i.name === itemName ? { ...i, count: i.isWeapon ? 1 : newCount } : i
    ));
  };

  const registerWeapons = useCallback(async () => {
    if (!selectedPlayer) {
      setError(t('refund.selectPlayer'));
      return;
    }

    if (pendingWeaponItems.length === 0) {
      setRegistrationMessage(t('refund.registrationNone'));
      return;
    }

    setRegisteringWeapons(true);
    setError(null);
    setRegistrationMessage(null);

    try {
      const response = await apiFetch('/api/refund/weapons/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          playerIdentifier: selectedPlayer.identifier,
          items: pendingWeaponItems.map((item) => ({
            name: item.name,
            label: item.label
          }))
        })
      });

      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Failed to register weapons');
      }

      const registeredByName = new Map((data.registeredItems || []).map((entry) => [entry.name, entry]));
      setSelectedItems((current) => current.map((item) => {
        if (!item.isWeapon || item.serial) {
          return item;
        }

        const registered = registeredByName.get(item.name);
        if (!registered) {
          return item;
        }

        return {
          ...item,
          serial: registered.serial,
          metadata: { serial: registered.serial }
        };
      }));

      setRegistrationMessage(t('refund.registrationSuccess', { count: (data.registeredItems || []).length }));
    } catch (err) {
      setError(t('refund.errorRegisteringWeapons', { error: err.message }));
    } finally {
      setRegisteringWeapons(false);
    }
  }, [apiFetch, pendingWeaponItems, selectedPlayer, t]);

  const giveRefund = useCallback(async () => {
    if (!selectedPlayer) {
      setError(t('refund.selectPlayer'));
      return;
    }
    if (selectedItems.length === 0) {
      setError(t('refund.addItems'));
      return;
    }

    const unresolvedWeapons = selectedItems.filter((item) => item.isWeapon && !item.serial);
    if (unresolvedWeapons.length > 0) {
      setError(t('refund.weaponNeedsRegistration'));
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const res = await apiFetch('/api/refund/give-items', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          playerIdentifier: selectedPlayer.identifier,
          items: selectedItems.map((item) => ({
            name: item.name,
            count: item.count,
            metadata: item.metadata || null
          }))
        })
      });

      const data = await res.json();
      setLastGiveResults(data);
      if (res.ok && data.success) {
        setSuccess(t('refund.refundSuccess', { player: selectedPlayer.name }));
        setSelectedItems([]);
        setSelectedPlayer(null);
      } else {
        setError(data.error || data.message || 'Failed to give refund');
      }
    } catch (err) {
      setError(t('refund.errorGivingRefund', { error: err.message }));
    } finally {
      setLoading(false);
    }
  }, [selectedPlayer, selectedItems, apiFetch, t]);

  const queueFailedItems = async () => {
    if (!lastGiveResults || !selectedPlayer) return;
    const failed = (lastGiveResults.results || []).filter(r => !r.success).map(r => ({ name: r.item, count: r.count, metadata: r.metadata || null }));
    if (!failed.length) return;

    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await apiFetch('/api/refund/queue', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ playerIdentifier: selectedPlayer.identifier, items: failed })
      });
      const data = await res.json();
      if (res.ok && data.success !== false) {
        setSuccess(t('refund.queuedFailedItems', { count: data.queued || data.items?.length || failed.length }));
      } else {
        setError(data.error || data.message || 'Failed to queue failed items');
      }
    } catch (err) {
      setError(t('refund.errorQueueingFailed', { error: err.message }));
    } finally {
      setLoading(false);
    }
  };

  if (!hasPermission('refund.edit')) {
    return (
      <div className="refund-container">
        <div className="refund-error">
          <p>{t('refund.noPermission')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="refund-container">
      <div className="refund-header">
        <h2>{t('refund.title')}</h2>
        <p>{t('refund.subtitle')}</p>
      </div>

      {error && <div className="refund-error">{error}</div>}
      {success && <div className="refund-success">{success}</div>}
      {lastGiveResults && Array.isArray(lastGiveResults.results) && lastGiveResults.results.length > 0 && (
        <div className="refund-results">
          <h4>{t('refund.resultsTitle', { count: lastGiveResults.results.length })}</h4>
          <div className="refund-results-list">
            {lastGiveResults.results.map((r, idx) => (
              <div key={idx} className={`refund-result-item ${r.success ? 'success' : 'failed'}`}>
                <div className="result-item-meta">
                  <strong>{r.item}</strong> x{r.count}
                </div>
                <div className="result-item-message">{r.message || (r.success ? t('refund.itemGiven') : t('refund.itemFailed'))}</div>
              </div>
            ))}
          </div>
          {(lastGiveResults.results || []).some(r => !r.success) && (
            <div className="refund-results-actions">
              <button className="btn btn-secondary" onClick={queueFailedItems} disabled={loading}>{t('refund.queueFailed')}</button>
            </div>
          )}
        </div>
      )}

      <div className="refund-layout">
        {/* Left: Player Selection */}
        <div className="refund-section">
          <div className="section-header">
            <h3>{t('refund.step1')}</h3>
          </div>

          {!selectedPlayer ? (
            <div className="player-search">
              <div className="search-box">
                <input
                  type="text"
                  value={searchInput}
                  onChange={handlePlayerSearch}
                  placeholder={t('refund.searchPlaceholder')}
                  className="search-input"
                />
              </div>
              {searchResults.length > 0 && (
                <div className="search-results">
                  {searchResults.map(player => (
                    <div
                      key={player.identifier}
                      className="search-result-item"
                      onClick={() => selectPlayer(player)}
                    >
                      <div className="player-info">
                        <div className="player-name">{player.name}</div>
                        <div className="player-id">{player.identifier}</div>
                      </div>
                      {player.phone && <div className="player-phone">{player.phone}</div>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="player-selected">
              <div className="selected-player-card">
                <div className="player-info">
                  <div className="player-name">{selectedPlayer.name}</div>
                  <div className="player-id">{selectedPlayer.identifier}</div>
                </div>
              </div>
              <button
                className="btn btn-secondary"
                onClick={clearPlayer}
              >
                {t('refund.changePlayer')}
              </button>
            </div>
          )}
        </div>

        {/* Middle: Item Selection */}
        <div className="refund-section">
          <div className="section-header">
            <h3>{t('refund.step2')}</h3>
            <button
              className="btn-refresh"
              onClick={loadItems}
              title={t('refund.refreshItems')}
            >
              ↻
            </button>
          </div>

          <div className="item-search-box">
            <input
              type="text"
              value={itemSearch}
              onChange={(e) => setItemSearch(e.target.value)}
              placeholder={t('refund.itemSearchPlaceholder')}
              className="search-input"
            />
          </div>

          <div className="available-items">
            {filteredItems.length > 0 ? (
              filteredItems.slice(0, 100).map(item => (
                <div
                  key={item.item_name}
                  className={`available-item ${isWeaponItem(item.item_name) ? 'weapon-item' : ''}`}
                  onClick={() => addItem(item)}
                >
                  <div className="item-detail">
                    <div className="item-label-row">
                      <div className="item-label">{item.label}</div>
                      {isWeaponItem(item.item_name) && (
                        <span
                          className={`item-badge weapon-badge ${canRefundWeapons ? 'allowed' : 'locked'}`}
                          title={canRefundWeapons ? t('refund.weaponRefundAllowed') : t('refund.weaponRefundLocked')}
                        >
                          {canRefundWeapons ? t('refund.weaponRefundAllowedShort') : t('refund.weaponRefundLockedShort')}
                        </span>
                      )}
                    </div>
                    <div className="item-name">{item.item_name}</div>
                  </div>
                  <button className="btn-add" disabled={isFirearmItem(item.item_name) && !canRefundWeapons} title={isFirearmItem(item.item_name) && !canRefundWeapons ? t('refund.weaponRefundLocked') : undefined}>+</button>
                </div>
              ))
            ) : (
              <div className="no-items">
                {itemSearch ? t('refund.noItemsFound') : t('refund.typeToSearch')}
              </div>
            )}
          </div>
        </div>

        {/* Middle: Weapon Registration */}
        <div className="refund-section">
          <div className="section-header">
            <h3>{t('refund.step3')}</h3>
            <span className="item-count">{t('refund.weaponCount', { count: firearmItems.length })}</span>
          </div>

          {registrationMessage && <div className="refund-success">{registrationMessage}</div>}

          {firearmItems.length > 0 ? (
            <div className="refund-list">
              {firearmItems.map(item => (
                <div key={item.name} className="refund-item weapon-registration-item">
                  <div className="item-info weapon-registration-info">
                    <div>
                      <div className="item-label">{item.label}</div>
                      <div className="item-name">{item.name}</div>
                    </div>
                    <div className={`weapon-serial-status ${item.serial ? 'registered' : 'pending'}`}>
                      {item.serial ? `${t('refund.weaponRegistered')}: ${item.serial}` : t('refund.weaponPendingRegistration')}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="no-items">{t('refund.registrationNone')}</div>
          )}

          {selectedPlayer && firearmItems.length > 0 && (
            <button
              className="btn btn-secondary btn-give-refund"
              onClick={registerWeapons}
              disabled={registeringWeapons || pendingWeaponItems.length === 0}
            >
              {registeringWeapons ? t('refund.registeringWeapons') : t('refund.registerWeapons')}
            </button>
          )}
        </div>

        {/* Right: Refund Confirmation */}
        <div className="refund-section">
          <div className="section-header">
            <h3>{t('refund.step4')}</h3>
            <span className="item-count">{t('refund.itemCount', { count: selectedItems.length })}</span>
          </div>

          {selectedItems.length > 0 ? (
            <div className="refund-list">
              {selectedItems.map(item => (
                <div key={item.name} className="refund-item">
                  <div className="item-info">
                    <div>
                      <div className="item-label">{item.label}</div>
                      <div className="item-name">{item.isFirearm ? item.serial || t('refund.weaponPendingRegistration') : item.name}</div>
                    </div>
                    {item.isFirearm ? (
                      <span className="weapon-serial-tag">{item.serial ? item.serial : t('refund.weaponPendingRegistration')}</span>
                    ) : (
                      <input
                        type="number"
                        min="1"
                        value={item.count}
                        onChange={(e) => updateItemCount(item.name, e.target.value)}
                        className="item-count-input"
                      />
                    )}
                  </div>
                  <button
                    className="btn-remove"
                    onClick={() => removeItem(item.name)}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="no-items">{t('refund.noItemsAdded')}</div>
          )}

          {selectedPlayer && selectedItems.length > 0 && (
            <button
              className="btn btn-primary btn-give-refund"
              onClick={giveRefund}
              disabled={loading}
            >
              {loading ? t('refund.givingRefund') : t('refund.giveRefund')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
